import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMixpanel, useNotify } from '../../../../hooks';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { useCarePlans } from '../../../../hooks/useCarePlans';

/**
 * Regenerating the Action Plan re-invokes the Zori Pulse scan for the member.
 * A manual pulse invocation runs the whole pulse, including the
 * `zori-pulse-coach` child that (re)drafts the action-plan items and the
 * coaching narrative shown on this page. There is no coach-only public
 * endpoint, so we trigger the parent scan and let the coach child run as part
 * of it.
 */
const ZORI_PULSE_SCAN_TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'expired',
]);
const ZORI_PULSE_SCAN_POLL_INTERVAL_MS = 5000;

const messages = defineMessages({
  started: {
    id: 'actionPlan.regenerate.pulse.started',
    defaultMessage:
      'Regeneration started. Zori is re-running the pulse for this member; the action plan will update in a few minutes.',
  },
  completed: {
    id: 'actionPlan.regenerate.pulse.completed',
    defaultMessage: 'Action plan regeneration complete.',
  },
  failed: {
    id: 'actionPlan.regenerate.pulse.failed',
    defaultMessage:
      'We could not regenerate the action plan. Please try again.',
  },
});

type ZoriPulseScanTask = {
  task_id: string;
  status: string;
};

export type UsePulseCoachRegenerationArgs = {
  patientId: string;
  // Project the pulse scan runs against; required to invoke a new scan.
  projectId: string;
  // Gate: only when the member has the Zori Pulse scan feature + policy do we
  // expose regeneration. When false the hook stays inert (no detect, no
  // invoke).
  enabled: boolean;
};

export type UsePulseCoachRegeneration = {
  triggerRegenerate: () => void;
  isRegenerating: boolean;
};

/**
 * Drives Action Plan "Regenerate" by re-invoking the member's Zori Pulse scan.
 *
 * Invokes `POST /v1/agents-v2/zori-pulse-scan/invoke`, polls the parent task to
 * completion, and refetches the member's care plans so the freshly-drafted
 * action items appear without a page reload. On mount it detects an already
 * in-flight scan so the button reflects the running state instead of letting an
 * operator trigger a duplicate (409).
 */
export const usePulseCoachRegeneration = ({
  patientId,
  projectId,
  enabled,
}: UsePulseCoachRegenerationArgs): UsePulseCoachRegeneration => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const mixpanel = useMixpanel();
  const apiClient = useApiClient();
  const { refetchCarePlans } = useCarePlans(patientId);

  const [scanTask, setScanTask] = React.useState<ZoriPulseScanTask | null>(
    null,
  );
  const [isInvoking, setIsInvoking] = React.useState(false);

  const triggerRegenerate = React.useCallback(async () => {
    if (!enabled || !patientId || !projectId) {
      notify(formatMessage(messages.failed), 'error');
      return;
    }

    setIsInvoking(true);
    let didSucceed = false;
    try {
      const response = await apiClient.request(
        'POST /v1/agents-v2/zori-pulse-scan/invoke',
        {
          subject_id: patientId,
          project_id: projectId,
        },
      );
      setScanTask({ task_id: response.data.task_id, status: 'pending' });
      didSucceed = true;
      notify(formatMessage(messages.started), 'success');
    } catch (err) {
      console.error(err);
      notify(formatMessage(messages.failed), 'error');
    } finally {
      setIsInvoking(false);
      mixpanel.track('action_plan_regenerate_requested', {
        patientId,
        didSucceed,
      });
    }
  }, [
    apiClient,
    enabled,
    formatMessage,
    mixpanel,
    notify,
    patientId,
    projectId,
  ]);

  // Poll the active scan until it reaches a terminal status, then refetch the
  // care plans so the regenerated action items render.
  React.useEffect(() => {
    if (!scanTask || ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status)) {
      return;
    }

    let isCancelled = false;

    const poll = async () => {
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations/:task_id',
          { task_id: scanTask.task_id },
        );
        if (isCancelled) {
          return;
        }
        const task = response.data;
        setScanTask(task);
        if (ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status)) {
          if (task.status === 'completed') {
            notify(formatMessage(messages.completed), 'success');
            refetchCarePlans();
          } else {
            notify(formatMessage(messages.failed), 'error');
          }
        }
      } catch {
        if (isCancelled) {
          return;
        }
        setScanTask(null);
        notify(formatMessage(messages.failed), 'error');
      }
    };

    const interval = window.setInterval(poll, ZORI_PULSE_SCAN_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [apiClient, formatMessage, notify, refetchCarePlans, scanTask]);

  // On mount (when regeneration is available), detect an already in-flight scan
  // so the button reflects the running state and resumes tracking it.
  React.useEffect(() => {
    if (!enabled || !patientId) {
      return;
    }

    let isCancelled = false;

    const detectInFlight = async () => {
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations',
          { subject_id: patientId },
        );
        if (isCancelled) {
          return;
        }
        const inFlight = response.data.find(
          task => !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status),
        );
        if (inFlight) {
          setScanTask(
            prev =>
              prev ?? { task_id: inFlight.task_id, status: inFlight.status },
          );
        }
      } catch {
        // Best-effort: ignore failures detecting an existing scan.
      }
    };

    void detectInFlight();

    return () => {
      isCancelled = true;
    };
  }, [apiClient, enabled, patientId]);

  const isRegenerating =
    isInvoking ||
    (!!scanTask && !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status));

  return { triggerRegenerate, isRegenerating };
};

export default usePulseCoachRegeneration;
